import React, { useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import menuStore from '../../utils/localForageInstance';
import { getSessionId } from '../../utils/sessionId';
import { apiUrls } from "../../utils/apiUrls";
import { callAPI } from "../../utils/apiUtils";
import { HTTP_STATUS } from "../../utils/httpStatusCodes";
import { toast } from 'react-toastify';
import { getDisplayPrice } from '../../utils/priceUtils';


const valueToIconMap = {
    "Vegan": "iconVegan",
    "Nut Free": "iconNutFree",
    "Dairy Free": "iconDairyFree",
    "Gluten Free": "iconGlutenFree",
    "Vegetarian": "iconVegetarian"
};
// Grouped ("new product config") edit modal: ingredients come from
// default_ingredients_groups / add_on_ingredients_groups / bases_ingredients_groups
// on the product, each row scoped to a size via size_price. Rendered by EditModal
// when productData.new_product_config is truthy (falls back to the legacy
// per-size base1/2/3_ingredient fields when a product has no bases groups defined).
const EditModalNew = ({ item, isOpen, onClose, onCartChange, orderType, editOrderId= null }) => {
    const [productData, setProductData] = useState(null);
    const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
    const [selectedSpiceIndex, setSelectedSpiceIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [totalPrice, setTotalPrice] = useState(0);
    const [sizeIngredientState, setSizeIngredientState] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectedSize = productData?.product_details_with_sizes?.[selectedSizeIndex] || {};
    const currentSizeIngredients = sizeIngredientState[selectedSizeIndex] || {
        defaultIngredients: [],
        addonIngredients: [],
        baseIngredients: {
            base_one: null,
            base_two: null,
            base_three: null
        }
    };
    const defaultIngredients = currentSizeIngredients.defaultIngredients;
    const addonIngredients = currentSizeIngredients.addonIngredients;
    const baseIngredients = currentSizeIngredients.baseIngredients;
    const labels = productData?.product_details_ingredients_name || {};
    const resolveGroupedPrice = (sizePrice, sizeId) => {
        if (!Array.isArray(sizePrice)) return parseFloat(sizePrice || 0);
        const match = sizePrice.find(sp => String(sp?.size?.id) === String(sizeId));
        return parseFloat((match || sizePrice[0])?.price || 0);
    };

    const ingredientGroups = [
        { key: 'default_ingredient', baseKey: 'default', label: labels.default_ingredient_label || 'Included ingredients', kind: 'default' },
        { key: 'addon_ingredient', baseKey: 'addon', label: labels.addon_ingredients_label || 'Add extra', kind: 'addon' },
        { key: 'base_one_ingredient', baseKey: 'base_one', label: labels.base_one_ingredient_label || 'Choice of base', type: 'radio', kind: 'bases' },
        { key: 'base_two_ingredient', baseKey: 'base_two', label: labels.base_two_ingredient_label || 'Choice of crust', type: 'radio', kind: 'bases' },
        { key: 'base_three_ingredient', baseKey: 'base_three', label: labels.base_three_ingredient_label || 'Choice of cheese', type: 'radio', kind: 'bases' },
    ];
    //checking group and conditions
    const defaultLabelFallback = labels.default_ingredient_label || 'Included ingredients';
    const addonLabelFallback = labels.addon_ingredients_label || 'Add extra';
    const buildGroupedSections = (groups, sizeId, kind) =>
        (groups || []).map(g => ({
            key: `${kind}_${g.group_id}`,
            baseKey: `${kind}_${g.group_id}`,
            label: g.label || (kind === 'default' ? defaultLabelFallback : addonLabelFallback),
            kind,
            items: (g.rows || []).map(r => ({
                ingredient: r.ingredient,
                price: resolveGroupedPrice(r.size_price, sizeId),
            })),
        })).filter(g => g.items.length);

    const buildBasesGroupSections = (groups, sizeId) =>
        (groups || []).map(g => ({
            key: `bases_${g.group_id}`,
            baseKey: `bases_${g.group_id}`,
            label: g.label || 'Choice of base',
            type: 'radio',
            kind: 'bases',
            items: (g.rows || []).map(r => ({
                ingredient: r.ingredient,
                price: resolveGroupedPrice(r.size_price, sizeId),
            })),
        })).filter(g => g.items.length);

    const groupedDefaults = buildGroupedSections(productData?.default_ingredients_groups, selectedSize?.id, 'default');
    const groupedAddons = buildGroupedSections(productData?.add_on_ingredients_groups, selectedSize?.id, 'addon');
    const groupedBases = buildBasesGroupSections(productData?.bases_ingredients_groups, selectedSize?.id);

    const displayGroups = [
        groupedDefaults.length ? groupedDefaults : [{ ...ingredientGroups[0], items: selectedSize?.default_ingredient || [] }],
        groupedAddons.length ? groupedAddons : [{ ...ingredientGroups[1], items: selectedSize?.addon_ingredient || [] }],
        groupedBases.length ? groupedBases : ingredientGroups.slice(2).map(g => ({ ...g, items: selectedSize?.[g.key] || [] })),
    ].flat();
    const initializeIngredients = (sizeData, productDetail = {}, sizeIndex, productMeta = productData) => {
        const flattenIngredients = arr => arr?.flat(Infinity) || [];
        const itemDefaultIngredients = flattenIngredients(productDetail.default_ingredients || []);
        const itemAddonIngredients = flattenIngredients(productDetail.addon_ingredients || []);

        const groupedRowsAsFlat = (groups) => (Array.isArray(groups) ? groups : []).flatMap(g => (Array.isArray(g.rows) ? g.rows : []).map(r => ({
            ingredient: r.ingredient,
            price: resolveGroupedPrice(r.size_price, sizeData?.id),
        })));
        const mergeFlatAndGrouped = (flatList, groups) => {
            const flat = Array.isArray(flatList) ? flatList : [];
            const seenIds = new Set(flat.map(i => String(i.ingredient?.id)));
            const extra = groupedRowsAsFlat(groups).filter(i => !seenIds.has(String(i.ingredient?.id)));
            return [...flat, ...extra];
        };
        const defaultSource = mergeFlatAndGrouped(sizeData.default_ingredient, productMeta?.default_ingredients_groups);
        const addonSource = mergeFlatAndGrouped(sizeData.addon_ingredient, productMeta?.add_on_ingredients_groups);

        const defaultQty = defaultSource.map(i => {
            const matched = itemDefaultIngredients.find(ci => ci.ingredient.id === i.ingredient.id);
            if (!matched) {
                console.warn('koi match ni mila bhai', i.ingredient.id);
            }
            return {
                id: i.ingredient.id,
                //qty: matched?.qty ?? 1,
                qty:
                matched !== undefined
                    ? Number(matched.qty ?? 0) // respect 0 or custom qty
                    : 1,                        // untouched default → 1
                price: parseFloat(i.price || 0),
                nutritional_information: i.ingredient.nutritional_information,
            };
        });

        const addonQty = addonSource.map(i => {
            const matched = itemAddonIngredients.find(ci => ci.ingredient.id === i.ingredient.id);
            return {
                id: i.ingredient.id,
                qty: matched?.qty ?? 0,
                price: parseFloat(i.price || 0),
                nutritional_information: i.ingredient.nutritional_information,
            };
        });

        const basesGroups = productMeta?.bases_ingredients_groups || [];
        let newBaseIngredients;
        if (basesGroups.length) {
            const savedBases = flattenIngredients(productDetail.bases_ingredients || []);
            newBaseIngredients = {};
            basesGroups.forEach(g => {
                const rowIds = new Set((g.rows || []).map(r => String(r.ingredient?.id)));
                const saved = savedBases.find(b => rowIds.has(String(b.ingredient?.id)));
                // Falls back to the group's first row when nothing was previously saved (a fresh
                // item, not an edit) - mirrors the legacy base_one/two/three fallback below
                // (sizeData.base_one_ingredient?.[0]?.ingredient?.id), which already defaults to
                // the first option instead of leaving the radio group unselected.
                const firstRowId = g.rows?.[0]?.ingredient?.id ?? null;
                newBaseIngredients[`bases_${g.group_id}`] = saved ? saved.ingredient.id : firstRowId;
            });
        } else {
            const base_one_id = productDetail.base1_ingredients?.[0]?.[0]?.ingredient?.id || sizeData.base_one_ingredient?.[0]?.ingredient?.id || null;
            const base_two_id = productDetail.base2_ingredients?.[0]?.[0]?.ingredient?.id || sizeData.base_two_ingredient?.[0]?.ingredient?.id || null;
            const base_three_id = productDetail.base3_ingredients?.[0]?.[0]?.ingredient?.id || sizeData.base_three_ingredient?.[0]?.ingredient?.id || null;

            newBaseIngredients = {
                base_one: base_one_id,
                base_two: base_two_id,
                base_three: base_three_id,
            };
        }

        setSizeIngredientState(prev => ({
            ...prev,
            [sizeIndex]: {
                defaultIngredients: defaultQty,
                addonIngredients: addonQty,
                baseIngredients: newBaseIngredients
            }
        }));
    };


    useEffect(() => {
        const fetchProductData = async () => {
            if (!item?.item_id) return;

            try {
                const data = await menuStore.getItem(`product_${item.item_id}`);
                if (!data) return;

                setProductData(data);
                const sizeIndex = data.product_details_with_sizes.findIndex(
                    size => String(size.id) === String(item.size)
                );
                const selectedIndex = sizeIndex !== -1 ? sizeIndex : 0;
                setSelectedSizeIndex(selectedIndex);
                setQuantity(item.item_quantity || 1);

                const sizeData = data.product_details_with_sizes[selectedIndex] || {};
                initializeIngredients(sizeData, item.product_detail || {}, selectedIndex, data);
            } catch (error) {
                console.error('Error loading product from localForage:', error);
            }
        };

        if (isOpen) {
            fetchProductData();
        }
    }, [item, isOpen]);

    const handleSizeChange = index => {
        setSelectedSizeIndex(index);
        const sizeData = productData?.product_details_with_sizes?.[index];
        if (!sizeIngredientState[index]) {
            initializeIngredients(sizeData, {}, index);
        }
    };

    const handleQtyChange = (type, ingredientType, id) => {
        setSizeIngredientState(prev => {
            const current = prev[selectedSizeIndex] || {
                defaultIngredients: [],
                addonIngredients: [],
                baseIngredients: {
                    base_one: null,
                    base_two: null,
                    base_three: null
                }
            };

            const listToUpdate = ingredientType === 'default' ? current.defaultIngredients : current.addonIngredients;
            const updatedList = listToUpdate.map(item => {
                if (item.id === id) {
                    let newQty = type === 'plus' ? item.qty + 1 : item.qty - 1;
                    //const minQty = ingredientType === 'default' ? 1 : 0;
                    //return { ...item, qty: newQty < minQty ? minQty : newQty };
                    return { ...item, qty: Math.max(newQty, 0) };
                }
                return item;
            });
            return {
                ...prev,
                [selectedSizeIndex]: {
                    ...current,
                    [ingredientType === 'default' ? 'defaultIngredients' : 'addonIngredients']: updatedList
                }
            };
        });
    };

    const handleSpiceChange = index => {
        setSelectedSpiceIndex(index);
    };

    const handleMainQtyChange = type => {
        setQuantity(prev => {
            let newQty = type === 'plus' ? prev + 1 : prev - 1;
            return newQty < 1 ? 1 : newQty;
        });
    };

    useEffect(() => {
        const currentSizeIngredients = sizeIngredientState[selectedSizeIndex] || {
            defaultIngredients: [],
            addonIngredients: [],
            baseIngredients: {
                base_one: null,
                base_two: null,
                base_three: null
            }
        };


        const defaultIngredients = currentSizeIngredients.defaultIngredients;
        const addonIngredients = currentSizeIngredients.addonIngredients;
        const baseIngredients = currentSizeIngredients.baseIngredients;
       // const basePrice = parseFloat(productData?.product_details_with_sizes?.[selectedSizeIndex]?.price || 0);
       const sizeData = productData?.product_details_with_sizes?.[selectedSizeIndex];
       const basePrice = parseFloat(
        getDisplayPrice(sizeData, orderType) || 0
      );
        const defaultTotal = defaultIngredients.reduce((sum, item) => {
            const extraQty = Math.max(item.qty - 1, 0);
            return sum + extraQty * item.price;
        }, 0);
        const addonTotal = addonIngredients.reduce((sum, item) => sum + item.qty * item.price, 0);
        const basesGroups = productData?.bases_ingredients_groups || [];
        const radioTotal = basesGroups.length
            ? basesGroups.reduce((sum, g) => {
                const selectedId = baseIngredients[`bases_${g.group_id}`];
                if (!selectedId) return sum;
                const row = (g.rows || []).find(r => String(r.ingredient?.id) === String(selectedId));
                return sum + (row ? resolveGroupedPrice(row.size_price, sizeData?.id) : 0);
            }, 0)
            : ['base_one', 'base_two', 'base_three'].reduce((sum, baseKey) => {
                const selectedId = baseIngredients[baseKey];
                const ingredientGroup = productData?.product_details_with_sizes?.[selectedSizeIndex]?.[`${baseKey}_ingredient`] || [];
                const selected = ingredientGroup.find(item => item.ingredient.id === selectedId);
                return sum + (selected ? parseFloat(selected.price || 0) : 0);
            }, 0);

        const total = (basePrice + defaultTotal + addonTotal + radioTotal) * quantity;
        setTotalPrice(total.toFixed(2));
    }, [sizeIngredientState, selectedSizeIndex, quantity, productData]);

    const handleBaseIngredientSelect = (group, ingredientId) => {
        setSizeIngredientState(prev => {
            const current = prev[selectedSizeIndex] || {
                defaultIngredients: [],
                addonIngredients: [],
                baseIngredients: {
                    base_one: null,
                    base_two: null,
                    base_three: null
                }
            };
            return {
                ...prev,
                [selectedSizeIndex]: {
                    ...current,
                    baseIngredients: {
                        ...current.baseIngredients,
                        [group]: ingredientId
                    }
                }
            };
        });
    };

    const handleUpdateCart = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const payload = new FormData();
        const { defaultIngredients, addonIngredients, baseIngredients } = sizeIngredientState[selectedSizeIndex] || {};
        payload.append('qty', quantity);
        payload.append('type', 'updated_price');
        payload.append('id', item.item_id);
        payload.append('default_ingredients_quantity', JSON.stringify(defaultIngredients));
        payload.append('add_on_ingredients_quantity', JSON.stringify(addonIngredients));

        // Legacy products post base1/2/3_ingredients individually; grouped ("new product
        // config") products post the same JSON-array shape CartService::formatDishAttributes()
        // already reads for bases_ingredients_quantity (mirrors default/add-on ingredients above).
        const basesGroups = productData?.bases_ingredients_groups || [];
        if (basesGroups.length) {
            const basesSel = basesGroups
                .map(g => ({ key: `bases_${g.group_id}`, id: baseIngredients[`bases_${g.group_id}`] }))
                .filter(b => b.id)
                .map(b => ({ id: b.id, qty: 1 }));
            if (basesSel.length) payload.append('bases_ingredients_quantity', JSON.stringify(basesSel));
        } else {
            if (baseIngredients.base_one) payload.append('base1_ingredients', baseIngredients.base_one);
            if (baseIngredients.base_two) payload.append('base2_ingredients', baseIngredients.base_two);
            if (baseIngredients.base_three) payload.append('base3_ingredients', baseIngredients.base_three);
        }

        const selectedSize = productData.product_details_with_sizes[selectedSizeIndex];
        if (selectedSize?.id) payload.append('size', selectedSize.id);

        const selectedSpice = productData.product_spices?.[selectedSpiceIndex];
        if (selectedSpice?.value) payload.append('chilli', selectedSpice.value);

        //  cart_id mil gayi to ise update karna hai
        const isEditMode = Boolean(item?.cart_id);
        const apiUrl = isEditMode ? apiUrls.UPDATE_CART_DETAIL : apiUrls.ADD_TO_CART_DETAIL;

        if (isEditMode) {
            payload.append('cart_id', item.cart_id);
        }
        try {
            console.log(payload)
            const session_id = getSessionId();
            payload.append('session_id', session_id);
            const phone_order =
            orderType == 'takeaway' || orderType == 'phone' ? 1 : 0;
        
            payload.append('phone_order', phone_order);
            payload.append('selected_customer_id', 1);
            if (editOrderId) {
                payload.append('edit_order_id', editOrderId);
            }
            const response = await callAPI(apiUrl, {}, "POST", payload, {});
            const data = response.data;
            // HTTP 200 alone isn't enough: update-cart can return 200 with status:false
            // (e.g. NotFoundHttpException) on the backend, so the body's status must be checked too.
            if (response.status === HTTP_STATUS.OK && data?.status) {
                if (onCartChange) onCartChange()
                onClose();
            } else {
                toast.error(data?.message || 'Failed to update cart');
            }

        } catch (error) {
            console.error('Error updating cart:', error);
            toast.error(error?.response?.data?.message || 'Failed to update cart');
        } finally {
            setIsSubmitting(false);
        }
    };
    if (!productData) return null;

    return (
        <CommonModal isOpen={isOpen} onClose={onClose} elId="mEditModal">
            <div class="modal-header">
                <h1 class="modal-title mdTitle" id="mEditModalLabel">Edit </h1>
                <button type="button" class="btn-close" onClick={onClose}></button>
            </div>
            <div class="modal-body">
                <div class="cardStyle d-none">
                    <div class="row g-3">
                        <div class="col-lg-7 col-xl-8">
                            <div class="row g-3 align-items-center">
                                <div class="col-sm-auto">
                                    <div class="proDetailImgCol">
                                        <img
                                            src={productData.product_img}
                                            alt={productData.product_name}
                                            className="proDetailImg"
                                        />
                                    </div>
                                </div>
                                <div class="col-sm">
                                    <div class="proDetailContent">
                                        <span className="lgTitle d-block">{productData.product_name}</span>
                                        <p className="mb-0">{productData.product_detail}</p>
                                        <div class="mProType justify-content-start">
                                            {productData.dish_preferences?.map((pref, index) => {
                                                const iconId = valueToIconMap[pref.value];
                                                if (!iconId) return null;

                                                return (
                                                    <svg key={index} width="26" height="26" className="typeIcon active">
                                                        <use xlinkHref={`./images/sprite.svg#${iconId}`}></use>
                                                    </svg>
                                                );
                                            })}
                                        </div>
                                        <span class="totalPriceLg fw-bold primaryColor">${totalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-5 col-xl-4">
                            <div class="d-flex flex-column gap-3">
                                <div class="d-flex gap-2">
                                    <span class="proDetailLbl">Nutritional Value</span>
                                    <span>
                                        {productData.nutritional_values?.length > 0 ? (
                                            productData.nutritional_values.map((nutri, index) => (
                                                <div>
                                                    {nutri.title}: {nutri.value}
                                                    <br />
                                                </div>
                                            ))
                                        ) : (
                                            'N/A'
                                        )}
                                    </span>
                                </div>
                                <div class="d-flex gap-2">
                                    <span class="proDetailLbl">Spice Level</span>
                                    <span class="proDetailVal">
                                        <div class="spiceLvlIcons">
                                            {productData.product_spices && productData.product_spices.length > 0 ? (
                                                productData.product_spices.map((spice, index) => {
                                                    const classMap = {
                                                        mild: "slMild",
                                                        medium: "slMedium",
                                                        hot: "slHot"
                                                    };
                                                    return (
                                                        <span key={index} className={`slIcon ${classMap[spice.value.toLowerCase()]}`}>
                                                            <svg width="7" height="12" className="spiceLvlIcon">
                                                                <use xlinkHref="./images/sprite.svg#iconChilli"></use>
                                                            </svg>
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span>NA</span>
                                            )}

                                        </div>
                                    </span>
                                </div>
                                <div class="d-flex gap-2">
                                    <span class="proDetailLbl">Size</span>
                                    <span class="proDetailVal">
                                        <div class="btnGroup">
                                            {productData.product_details_with_sizes?.map((size, index) => {
                                                const id = `sizeRadio${index}`;
                                                return (
                                                    <React.Fragment key={size.id}>
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="sizeRadio"
                                                            id={id}
                                                            autoComplete="off"
                                                            defaultChecked={index === 0}
                                                        />
                                                        <label className="radioBtn" htmlFor={id}>
                                                            {size.abbreviation || size.title}
                                                        </label>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </span>
                                </div>
                                <div class="d-flex gap-2">
                                    <span class="proDetailLbl">Quantity</span>
                                    <span class="proDetailVal">
                                        <div class="quantitySelector qsLg">
                                            <span class="qtyBtn minus">
                                                <svg width="8" height="8">
                                                    <use xlinkHref="./images/sprite.svg#iconMinus" onClick={() => handleMainQtyChange('minus')}></use>
                                                </svg>
                                            </span>
                                            <input type="number" class="form-control quantityInput" value={quantity} min="1" />
                                            <span class="qtyBtn plus" onClick={() => handleMainQtyChange('plus')}>
                                                <svg width="8" height="8" class="plus">
                                                    <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                                                </svg>
                                            </span>
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="cardStyle mb-5 mb-sm-4">
                    <div class="row g-3">
                        <div class="col-lg-7 col-xl-8">
                            <div class="row g-3 align-items-center">
                                <div class="col-sm-auto">
                                    <div class="proDetailImgCol">
                                        <img src={productData.product_img}
                                            alt={productData.product_name} class="proDetailImg" />
                                    </div>
                                </div>
                                <div class="col-sm">
                                    <div class="proDetailContent">
                                        <span class="lgTitle d-block">{productData.product_name} <span class="totalPriceLg fw-bold primaryColor">${totalPrice}</span></span>
                                        <p class="mb-0">{productData.product_detail}</p>
                                        <div class="d-flex flex-column gap-3">
                                            <div class="d-flex gap-2">
                                                <span class="proDetailLbl">Spice Level</span>
                                                <span class="proDetailVal">
                                                    <div className="btnGroup spiceLvlIcons">
                                                        {productData.product_spices && productData.product_spices.length > 0 ? (
                                                            productData.product_spices.map((spice, index) => {
                                                                const classMap = {
                                                                    mild: "slMild",
                                                                    medium: "slMedium",
                                                                    hot: "slHot"
                                                                };
                                                                const id = `spiceRadio${index}`;
                                                                return (
                                                                    <React.Fragment key={spice.value}>
                                                                        <input
                                                                            type="radio"
                                                                            className="btn-check"
                                                                            name="spiceRadio"
                                                                            id={id}
                                                                            autoComplete="off"
                                                                            checked={selectedSpiceIndex === index}
                                                                            onChange={() => handleSpiceChange(index)}
                                                                        />
                                                                        <label className={`radioBtn ${classMap[spice.value.toLowerCase()]}`} htmlFor={id}>
                                                                            {spice.title}
                                                                        </label>
                                                                    </React.Fragment>
                                                                );
                                                            })
                                                        ) : (
                                                            <span>NA</span>
                                                        )}
                                                    </div>
                                                </span>
                                            </div>
                                            <div class="d-flex gap-2">
                                                <span class="proDetailLbl">Quantity</span>
                                                <span class="proDetailVal">
                                                    <div class="quantitySelector qsLg">
                                                        <span class="qtyBtn minus" onClick={() => handleMainQtyChange('minus')}>
                                                            <svg width="8" height="8">
                                                                <use xlinkHref="./images/sprite.svg#iconMinus"></use>
                                                            </svg>
                                                        </span>
                                                        <input type="number" class="form-control quantityInput" value={quantity} min="1" />
                                                        <span class="qtyBtn plus" onClick={() => handleMainQtyChange('plus')}>
                                                            <svg width="8" height="8" class="plus">
                                                                <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                </span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-5 col-xl-4">
                            <div class="d-flex flex-column gap-2">
                                <div class="d-flex gap-2">
                                    <span class="proDetailLbl">Nutritional Value</span>
                                    <span>
                                        {productData.nutritional_values?.length > 0 ? (
                                            productData.nutritional_values.map((nutri, index) => (
                                                <div>
                                                    {nutri.title}: {nutri.value}
                                                    <br />
                                                </div>

                                            ))
                                        ) : (
                                            'N/A'
                                        )}
                                    </span>

                                </div>
                                <div class="d-flex flex-column gap-2">
                                    <span class="proDetailLbl">Size</span>
                                    <span class="proDetailVal">
                                        <div className="btnGroup">
                                            {productData.product_details_with_sizes?.map((size, index) => {
                                                const id = `sizeRadio${index}`;
                                                return (
                                                    <React.Fragment key={size.id}>
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="sizeRadio"
                                                            id={id}
                                                            autoComplete="off"
                                                            checked={selectedSizeIndex === index}
                                                            onChange={() => handleSizeChange(index)}
                                                        />
                                                        <label className="radioBtn" htmlFor={id} onClick={() => handleSizeChange(index)}>
                                                            {size.abbreviation || size.title}
                                                        </label>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-column gap-2 mb-3">
                    {displayGroups.map(({ key, label, type = 'quantity', kind, items, baseKey: groupBaseKey }) => {
                        const ingredients = items;
                        if (!ingredients.length) return null;


                        const radioGroupName = `${key}_group`;
                        const caption = kind === 'bases' ? 'Choose your bases' : kind === 'addon' ? 'Extras to personalise your order' : 'Included with this item';

                        return (
                            <IngredientAccordion key={key} title={label} caption={caption}>
                                {ingredients.map((ingredientData, idx) => {
                                            const ingredient = ingredientData?.ingredient;
                                            const ingredientName = ingredient?.name || 'Unnamed';
                                            const price = parseFloat(ingredientData?.price || 0).toFixed(2);
                                            const priceText = price > 0 ? ` (+$${price})` : ' (+$0)';
                                            const inputId = `${radioGroupName}_${idx}`;
                                            const isAddon = kind === 'addon';
                                            const isDefault = kind === 'default';
                                            const ingredientType = isAddon ? 'addon' : 'default';


                                            const foundIngredient = isDefault
                                                ? defaultIngredients.find(d => d.id === ingredient?.id)
                                                : addonIngredients.find(d => d.id === ingredient?.id);


                                            //const currentQty = foundIngredient ? foundIngredient.qty : (isDefault ? 1 : 0);
                                            const currentQty = foundIngredient ? foundIngredient.qty : 0;

                                            const baseKey = groupBaseKey;

                                            return (
                                                <div className="lblValList" key={`${ingredient?.id}-${idx}`}>
                                                    {type === 'radio' ? (
                                                        <>
                                                            <span className="listLbl">
                                                                <label className="form-check-label" htmlFor={inputId}>
                                                                    {ingredientName}{priceText}
                                                                </label>
                                                            </span>
                                                            <span className="listVal">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={radioGroupName}
                                                                    id={inputId}
                                                                    checked={baseIngredients[baseKey] === ingredient?.id}
                                                                    onChange={() => handleBaseIngredientSelect(baseKey, ingredient?.id)}
                                                                />
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="listLbl">
                                                                {ingredientName}
                                                                {price > 0 ? ` (+$${price})` : ''}
                                                            </span>
                                                            <span className="listVal">
                                                                <div className="quantitySelector">
                                                                    <span className="qtyBtn minus" onClick={() => handleQtyChange('minus', ingredientType, ingredient?.id)}>
                                                                        <svg width="8" height="8">
                                                                            <use xlinkHref="./images/sprite.svg#iconMinus"></use>
                                                                        </svg>
                                                                    </span>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control quantityInput"
                                                                        value={currentQty}
                                                                       // min={isDefault ? 1 : 0}
                                                                         min={0}
                                                                        readOnly
                                                                    />
                                                                    <span className="qtyBtn plus" onClick={() => handleQtyChange('plus', ingredientType, ingredient?.id)}>
                                                                        <svg width="8" height="8">
                                                                            <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                                                                        </svg>
                                                                    </span>
                                                                </div>
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                })}
                            </IngredientAccordion>
                        );
                    })}
                </div>

            </div>
            <div class="modal-footer justify-content-between">
                <div class="d-flex flex-wrap flex-sm-column gap-sm-1 gap-2 mbFullWd justify-content-center align-items-center">
                    <div class="mProType justify-content-start">
                        {productData.dish_preferences?.map((pref, index) => {
                            const iconId = valueToIconMap[pref.value];
                            if (!iconId) return null;

                            return (
                                <svg key={index} width="26" height="26" className="typeIcon active">
                                    <use xlinkHref={`./images/sprite.svg#${iconId}`}></use>
                                </svg>
                            );
                        })}
                    </div>
                    <span class="d-block text-center">Total Price: <span class="fw-bold primaryColor">${totalPrice}</span></span>
                </div>
                <button type="button" class="btn btnLight order-sm-first " onClick={onClose}>Close</button>
                <button
                    type="button"
                    className="btn btnPrimary"
                    onClick={handleUpdateCart}
                    disabled={isSubmitting}
                    style={isSubmitting ? { opacity: 0.7, pointerEvents: 'none' } : {}}
                >
                    {isSubmitting ? (
                        <>
                            <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />
                            Adding...
                        </>
                    ) : 'Update Cart'}
                </button>
            </div>
        </CommonModal>
    );
};

/* Collapsed-by-default accordion row for a single ingredient/base group - matches the
   customer-facing product-details page's section layout (title + static caption + chevron,
   collapsed until clicked) instead of always-expanded cards. */
const IngredientAccordion = ({ title, caption, children }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className={`ingAcc${open ? ' open' : ''}`}>
            <button type="button" className="ingAcc-hd" onClick={() => setOpen(o => !o)}>
                <span className="ingAcc-titleWrap">
                    <span className="ingAcc-title">{title}</span>
                    <span className="ingAcc-caption">{caption}</span>
                </span>
                <svg className="ingAcc-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <div className="ingAcc-body">
                <div className="ingAcc-inner">
                    <div className="ingAcc-pad">{children}</div>
                </div>
            </div>
        </div>
    );
};

export default EditModalNew;